import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import debounce from "lodash/debounce";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import CommonPurchaseFields from "../CommonPurchaseFields";
import ProductTable from "../../../Common/ProductTable";
import ChargesSection from "../../../Common/ChargesSection";
import { CHARGE_OPTIONS, UNIT_OPTIONS } from "../../../Common/constants";

// Helper function
const createEmptyCharge = () => ({
  id: Date.now(),
  description: "",
  chargepercent: 0,
  amount: 0,
  gstPercent: 0,
  isPercentBased: true,
});

const GRNTOPMDConvert = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [commonFieldsData, setcommonFieldsData] = useState({
    supplierinvoicedate: "",
    supplierinvoiceno: "",
    status: "",
    shipto: "",
    otherreference: "",
    deliverynoteno: "",
    deliverynotedate: "",
    supplier: null,
    supplierDetails: {
      contactperson: "",
      contactno: "",
      email: "",
    },
    supplierMultipleAddress: null,
    branch: null,
    warehouse: null,
  });

  // Product and charges state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
  const [rows, setRows] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([createEmptyCharge()]);
  const [loadingPrices, setLoadingPrices] = useState({});

  const [purchaseinvoicedate, setPurchaseinvoicedate] = useState("");
  const [purchaseinvoiceno, setPurchaseinvoiceno] = useState("");
  const [enquiryValidationMsg, setEnquiryValidationMsg] = useState("");
  const [isEnquiryValid, setIsEnquiryValid] = useState(false);

  // Validate purchase number
  const checkPurchaseInvoiceNo = debounce(async (number) => {
    if (!number) {
      setEnquiryValidationMsg("");
      setIsEnquiryValid(false);
      return;
    }

    if (!/^[A-Za-z0-9-]+$/.test(number)) {
      setEnquiryValidationMsg("❌ Only alphanumeric characters and hyphens are allowed");
      setIsEnquiryValid(false);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}purchase/check/${number}`, {
        headers: authHeader(),
      });

      if (response.data === true) {
        setEnquiryValidationMsg("❌ This purchase invoice number already exists.");
        setIsEnquiryValid(false);
      } else {
        setEnquiryValidationMsg("✅ This purchase invoice number is available.");
        setIsEnquiryValid(true);
      }
    } catch (error) {
      setEnquiryValidationMsg("❌ Error validating purchase invoice number.");
      setIsEnquiryValid(false);
    }
  }, 400);

  useEffect(() => {
    if (purchaseinvoiceno) {
      checkPurchaseInvoiceNo(purchaseinvoiceno);
    }
  }, [purchaseinvoiceno]);

  const [jobnumber, setjobnumber] = useState("");

  // API Functions
  const getPumpProductData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}product/page/ptype/0/10/desc/id/1/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
        { headers: authHeader() }
      );
      const products = response.data.Index || response.data.data || [];
      setProductOptions(
        products.map((product) => ({
          value: product.id,
          label: `${product.description} - ${product.moc} - ${product.size} - ${product.tagnumber} - ${product.serialnumber} - ${product.partno}`,
          description: product.description,
          uom: product.unitofmeasurement || UNIT_OPTIONS[0].value,
          igst: product.igst || 0,
          partno: product.partno,
          serialnumber: product.serialnumber,
          modelnumber: product.modelnumber,
          tagnumber: product.tagnumber,
          hsnsacno: product.hsnsacno,
          moc: product.moc || "",
          type: product.type || "",
          size: product.size || "",
          rating: product.rating || "",
          classes: product.classes || "",
          schedule: product.schedule || "",
          certification: product.certification || "",
          unitrateinr: product.unitrateinr || 0,
        }))
      );
    } catch (error) {
      Swal.fire("Error", "Failed to fetch product data", "error");
    }
  };

  const fetchLastPurchasePrice = async (supplierId, productId) => {
    try {
      if (!supplierId || !productId) return null;

      setLoadingPrices(prev => ({ ...prev, [productId]: true }));

      const response = await axios.get(
        `${BASE_URL}purchase/historyPrice/supplierandproduct/${supplierId}/${productId}`,
        { headers: authHeader() }
      );

      const price = response.data.price || response.data.unitPrice || response.data;
      return typeof price === 'number' ? price : null;
    } catch (error) {
      console.error("Error fetching last purchase price:", error);
      return null;
    } finally {
      setLoadingPrices(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Fetch existing purchase data
  useEffect(() => {
    const fetchPurchaseData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}materialReceiptNote/findById/${id}`, {
          headers: authHeader(),
        });
        const data = response.data;

        // Set basic fields
        setPurchaseinvoicedate(data.purchaseinvoicedate);
        setjobnumber(data.jobnumber);


        // Set PO data
        setcommonFieldsData({
          supplierinvoicedate: data.supplierinvoicedate,
          supplierinvoiceno: data.supplierinvoiceno,
          status: data.status,
          shipto: data.shipto,
          otherreference: data.otherreference,
          deliverynoteno: data.deliverynoteno,
          deliverynotedate: data.deliverynotedate,
          supplier: data.supplier ? {
            value: data.supplier.id,
            label: data.supplier.companyname
          } : null,
          supplierDetails: {
            contactperson: data.contactperson,
            contactno: data.contactno,
            email: data.email,
          },
          supplierMultipleAddress: data.supplierMultipleAddress ? {
            value: data.supplierMultipleAddress.id,
            label: data.supplierMultipleAddress.supplieraddress
          } : null,
          branch: data.branch ? {
            value: data.branch.id,
            label: data.branch.branchname
          } : null,
          warehouse: data.warehouse ? {
            value: data.warehouse.id,
            label: data.warehouse.warehousename
          } : null,
        });

        // Set product rows
        if (data.materialReceiptNoteItems && data.materialReceiptNoteItems.length > 0) {
          const productRows = data.materialReceiptNoteItems.map(item => ({
            description: item.product.description,
            value: item.product.id,
            label: `${item.product.description} - ${item.product.moc || ''} - ${item.product.size || ''}`,
            productremarks: item.productremarks,
            quantity: item.quantityreceived,
            uom: item.product.unitofmeasurement || UNIT_OPTIONS[0].value,
            unitRateInr: item.unitrateinr,
            gstPercent: item.product.igst,
            amount: item.amount,
            totalinr: item.totalinr,
            hsnsacno: item.product.hsnsacno,
            partno: item.product.partno,
            serialnumber: item.product.serialnumber,
            moc: item.moc,
            type: item.product.type,
            size: item.size,
            rating: item.rating,
            classes: item.classes,
            schedule: item.schedule,
            certification: item.certification,
          }));
          setRows(productRows);
        }

        // Set additional charges
        if (data.additionalCharges && data.additionalCharges.length > 0) {
          const netTotal = data.materialReceiptNoteItems.reduce((sum, item) => sum + (item.totalinr || 0), 0);
          const charges = data.additionalCharges.map(charge => ({
            id: Date.now() + Math.random(),
            description: charge.chargetype,
            chargepercent: charge.chargepercent,
            amount: charge.isPercentBased ? (charge.chargepercent * netTotal) / 100 : charge.chargeamount,
            gstPercent: charge.chargegst,
            isPercentBased: charge.isPercentBased,
          }));
          setAdditionalCharges(charges);
        } else {
          setAdditionalCharges([createEmptyCharge()]);
        }

      } catch (error) {
        Swal.fire("Error", "Failed to fetch purchase data", "error");
        navigate("/pindex/1");
      } finally {
        setIsFetching(false);
      }
    };

    if (id) {
      fetchPurchaseData();
    }
  }, [id]);

  const handleProductSearchChange = debounce((inputValue) => {
    setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
  }, 500);

  // Product data effect
  useEffect(() => {
    getPumpProductData();
  }, [productSearch]);

  // Update product prices when supplier changes
  useEffect(() => {
    const updateProductPrices = async () => {
      const supplierId = commonFieldsData.supplier?.value;
      if (!supplierId) return;

      const updatedRows = [...rows];

      for (let i = 0; i < updatedRows.length; i++) {
        const row = updatedRows[i];
        const price = await fetchLastPurchasePrice(supplierId, row.value);

        if (price !== null) {
          updatedRows[i].unitRateInr = price;
          updatedRows[i].lastPurchasePrice = price;

          // Recalculate amount and total
          const quantity = parseFloat(updatedRows[i].quantity) || 0;
          const gstPercent = parseFloat(updatedRows[i].gstPercent) || 0;

          const amount = quantity * price;
          const gstAmount = (amount * gstPercent) / 100;
          const totalinr = amount + gstAmount;

          updatedRows[i].amount = amount;
          updatedRows[i].totalinr = totalinr;
        }
      }

      setRows(updatedRows);
    };

    updateProductPrices();
  }, [commonFieldsData.supplier]);

  // Product handlers
  const handleProductChange = async (selectedOption) => {
    setSelectedProduct(selectedOption);
    const selectedProductData = productOptions.find(
      (product) => product.value === selectedOption.value
    );

    if (selectedProductData) {
      const supplierId = commonFieldsData.supplier?.value;
      let lastPurchasePrice = 0;

      if (supplierId) {
        lastPurchasePrice = await fetchLastPurchasePrice(supplierId, selectedProductData.value) || 0;
      }

      const newRow = createProductRow(selectedProductData, lastPurchasePrice);
      setRows([...rows, newRow]);
    }
  };

  const createProductRow = (productData, lastPurchasePrice) => ({
    description: productData.description,
    value: productData.value,
    label: productData.label,
    productremarks: "",
    quantity: "",
    uom: productData.uom || "",
    unitRateInr: lastPurchasePrice || productData.unitrateinr || 0,
    gstPercent: productData.igst || 0,
    amount: (lastPurchasePrice || productData.unitrateinr || 0),
    totalinr: (lastPurchasePrice || productData.unitrateinr || 0) * (1 + (productData.igst || 0) / 100),
    hsnsacno: productData.hsnsacno,
    partno: productData.partno,
    serialnumber: productData.serialnumber,
    moc: productData.moc || "",
    type: productData.type || "",
    size: productData.size || "",
    rating: productData.rating || "",
    classes: productData.classes || "",
    schedule: productData.schedule || "",
    certification: productData.certification || "",
    lastPurchasePrice: lastPurchasePrice,
  });

  const handleRowChange = (index, key, value) => {
    const updatedRows = [...rows];
    updatedRows[index][key] = value;

    if (["quantity", "unitRateInr", "gstPercent"].includes(key)) {
      const quantity = parseFloat(updatedRows[index].quantity) || 0;
      const unitRateInr = parseFloat(updatedRows[index].unitRateInr) || 0;
      const gstPercent = parseFloat(updatedRows[index].gstPercent) || 0;

      const amount = quantity * unitRateInr;
      const gstAmount = (amount * gstPercent) / 100;
      const totalinr = amount + gstAmount;

      updatedRows[index].amount = amount;
      updatedRows[index].totalinr = totalinr;
    }

    setRows(updatedRows);
  };

  const removeRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  // Charges handlers - Fixed for 2 decimal places
  const handleChargeChange = (index, field, value, isBlur = false) => {
    setAdditionalCharges(prev => {
      const newCharges = [...prev];
      const charge = { ...newCharges[index] };
      const netTotal = rows.reduce((sum, row) => sum + (row.totalinr || 0), 0);

      if (field === "chargepercent") {
        const percentage = Math.min(value, 100);
        charge.chargepercent = parseFloat(percentage.toFixed(2));

        if (isBlur || percentage === 100) {
          const calculatedAmount = (percentage * netTotal) / 100;
          charge.amount = parseFloat(calculatedAmount.toFixed(2));
        } else {
          charge.amount = 0;
        }
      } else if (field === "amount") {
        charge.amount = parseFloat(value.toFixed(2));
        charge.chargepercent = 0;
      } else if (field === "gstPercent") {
        charge.gstPercent = parseFloat(value.toFixed(2));
      } else {
        charge[field] = value;
      }

      newCharges[index] = charge;
      return newCharges;
    });
  };

  const addNewChargeRow = () => {
    setAdditionalCharges((prev) => [...prev, createEmptyCharge()]);
  };

  const removeChargeRow = (index) => {
    if (additionalCharges.length > 1) {
      setAdditionalCharges((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Calculations - Fixed for 2 decimal places
  const { productsTotal, additionalChargesTotal, grossTotal, grandTotal } = useMemo(() => {
    const productsTotal = rows.reduce((total, row) => {
      const rowTotal = (row.quantity || 0) * (row.unitRateInr || 0) * (1 + (row.gstPercent || 0) / 100);
      return total + rowTotal;
    }, 0);

    const additionalChargesTotal = additionalCharges.reduce((total, charge) => {
      const chargeTotal = (charge.amount || 0) * (1 + (charge.gstPercent || 0) / 100);
      return total + chargeTotal;
    }, 0);

    const grossTotal = productsTotal + additionalChargesTotal;
    const roundedGrossTotal = Math.round(grossTotal);
    const grandTotal = roundedGrossTotal;

    return {
      productsTotal: parseFloat(productsTotal.toFixed(2)),
      additionalChargesTotal: parseFloat(additionalChargesTotal.toFixed(2)),
      grossTotal: parseFloat(grossTotal.toFixed(2)),
      grandTotal
    };
  }, [rows, additionalCharges]);

  // Form validation
  const validateForm = () => {
    if (!commonFieldsData.supplier) {
      Swal.fire("Missing Supplier", "Please select a supplier before submitting the form.", "error");
      return false;
    }

    if (!commonFieldsData.supplierMultipleAddress) {
      Swal.fire("Missing Address", "Please select the supplier's address.", "error");
      return false;
    }

    if (rows.length === 0) {
      Swal.fire("No Products Added", "Please add at least one product to continue.", "error");
      return false;
    }

    for (const row of rows) {
      if (!row.quantity || isNaN(row.quantity) || row.quantity <= 0) {
        Swal.fire(
          "Invalid Quantity",
          "Each product must have a valid quantity greater than 0.",
          "error"
        );
        return false;
      }
    }

    return true;
  };

  // Updated handleSubmit for convert request
  const handleSubmit = async () => {
    if (!isEnquiryValid) {
      Swal.fire("Error", "Please enter a valid purchase number", "error");
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);

    // Prepare purchase items
    const purchaseItems = rows.map((row) => ({
      product: { id: row.value },
      quantity: row.quantity,
      uom: row.uom,
      unitrateinr: row.unitRateInr,
      gst: row.gstPercent,
      amount: row.amount,
      totalinr: row.totalinr,
      productremarks: row.productremarks,
      partno: row.partno || "",
      hsnsacno: row.hsnsacno || "",
      serialnumber: row.serialnumber || "",
      moc: row.moc || "",
      type: row.type || "",
      size: row.size || "",
      rating: row.rating || "",
      classes: row.classes || "",
      schedule: row.schedule || "",
      certification: row.certification || "",
    }));

    // Prepare additional charges - Fixed for 2 decimal places
    const additionalChargesData = additionalCharges
      .filter((charge) => charge.description && (charge.amount > 0 || charge.chargepercent > 0))
      .map((charge) => {
        const chargeAmount = parseFloat(charge.amount.toFixed(2));
        const gstPercent = parseFloat(charge.gstPercent.toFixed(2));
        const gstAmount = parseFloat((chargeAmount * gstPercent / 100).toFixed(2));
        const chargetotal = parseFloat((chargeAmount + gstAmount).toFixed(2));

        return {
          chargetype: charge.description,
          chargepercent: parseFloat(charge.chargepercent.toFixed(2)),
          chargeamount: chargeAmount,
          chargegst: gstPercent,
          chargetotal: chargetotal,
          isPercentBased: charge.isPercentBased,
        };
      });

    // Calculate totals - Fixed for 2 decimal places
    const additionalChargesTotalCalc = parseFloat(additionalChargesData.reduce(
      (sum, charge) => sum + charge.chargetotal,
      0
    ).toFixed(2));

    const productsTotalCalc = parseFloat(purchaseItems.reduce(
      (sum, item) => sum + item.totalinr,
      0
    ).toFixed(2));

    const grossTotalCalc = parseFloat((productsTotalCalc + additionalChargesTotalCalc).toFixed(2));

    const payload = {
      purchaseItem: purchaseItems,
      additionalCharges: additionalChargesData.length > 0 ? additionalChargesData : null,
      nettotalproduct: productsTotalCalc,
      additionalchargestotal: additionalChargesTotalCalc,
      grosstotal: grossTotalCalc,
      grandtotalproduct: grossTotalCalc,
      purchasetype: "Manufacturing",
      purchasecategory: "Domestic",
      purchaseinvoiceno,
      jobnumber,
      purchaseinvoicedate,
      supplierinvoicedate: commonFieldsData.supplierinvoicedate,
      supplierinvoiceno: commonFieldsData.supplierinvoiceno,
      status: commonFieldsData.status,
      shipto: commonFieldsData.shipto,
      otherreference: commonFieldsData.otherreference,
      deliverynoteno: commonFieldsData.deliverynoteno,
      deliverynotedate: commonFieldsData.deliverynotedate,
      supplier: commonFieldsData.supplier ? { id: commonFieldsData.supplier.value } : null,
      contactperson: commonFieldsData.supplierDetails?.contactperson,
      contactno: commonFieldsData.supplierDetails?.contactno,
      email: commonFieldsData.supplierDetails?.email,
      supplierMultipleAddress: commonFieldsData.supplierMultipleAddress
        ? { id: commonFieldsData.supplierMultipleAddress.value }
        : null,
      branch: commonFieldsData.branch ? { id: commonFieldsData.branch.value } : null,
      warehouse: commonFieldsData.warehouse ? { id: commonFieldsData.warehouse.value } : null,
    };

    try {
      // Use special convert endpoint
      const response = await axios.post(`${BASE_URL}purchase/mrnToPurchase/${id}`, payload, {
        headers: authHeader(),
      });
      Swal.fire("Success", "GRN to Purchase converted successfully", "success");
      navigate("/pindex/1");
    } catch (error) {
      console.error("Convert error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to convert GRN to Purchase",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    getPumpProductData();
  }, [productSearch]);

  const handleCommonDataChange = (data) => {
    setcommonFieldsData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  if (isFetching) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container">
      <Form>
        {/* Purchase Invoice Date Only - Invoice No remove kiya */}
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Purchase Invoice No *</Form.Label>
              <Form.Control
                type="text"
                value={purchaseinvoiceno}
                onChange={(e) => setPurchaseinvoiceno(e.target.value)}
                isInvalid={!!enquiryValidationMsg.includes("❌")}
                isValid={!!enquiryValidationMsg.includes("✅")}
              />
              <Form.Control.Feedback
                type={enquiryValidationMsg.includes("✅") ? "valid" : "invalid"}
              >
                {enquiryValidationMsg}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Purchase Invoice Date</Form.Label>
              <Form.Control
                type="date"
                value={purchaseinvoicedate}
                onChange={(e) => setPurchaseinvoicedate(e.target.value)}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Job Number</Form.Label>
              <Form.Control
                type="text"
                value={jobnumber}
                onChange={(e) => setjobnumber(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <CommonPurchaseFields
          initialData={commonFieldsData}
          onDataChange={handleCommonDataChange}
        />

        {/* Products Section */}
        <div className="mt-4">
          <h5>Products</h5>
          <Row>
            <Col md="12">
              <Form.Group>
                <Form.Label>Select Product</Form.Label>
                <Select
                  value={selectedProduct}
                  onChange={handleProductChange}
                  options={productOptions}
                  placeholder="Search and select product"
                  onInputChange={handleProductSearchChange}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          <ProductTable
            rows={rows}
            unitOptions={UNIT_OPTIONS}
            onRowChange={handleRowChange}
            onRemoveRow={removeRow}
            loadingPrices={loadingPrices}
          />
        </div>

        {/* Charges Section */}
        <ChargesSection
          additionalCharges={additionalCharges}
          chargeOptions={CHARGE_OPTIONS}
          productsTotal={productsTotal}
          additionalChargesTotal={additionalChargesTotal}
          grossTotal={grossTotal}
          grandTotal={grandTotal}
          onChargeChange={handleChargeChange}
          onAddCharge={addNewChargeRow}
          onRemoveCharge={removeChargeRow}
        />

        <div className="d-flex justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-primary me-2"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Converting...' : 'CONVERT GRN TO PURCHASE'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
        </div>
      </Form>
    </div>
  );
};

export default GRNTOPMDConvert;